import { useEffect, useState } from "react";
import { getLang } from "./i18n";

/** Parse date-only strings ("2026-07-13") as LOCAL noon so they never shift
    a day for users west of UTC; full ISO timestamps parse normally. */
function parseLocal(iso: string): Date {
  return /^\d{4}-\d{2}-\d{2}$/.test(iso) ? new Date(iso + "T12:00:00") : new Date(iso);
}

/** The locale that matches the contractor's chosen UI language, so dates read
    "7月13日" in Chinese mode and "Jul 13" in English. */
function locale() { return getLang() === "zh" ? "zh-CN" : "en-US"; }

export function fmtDate(iso?: string, opts: Intl.DateTimeFormatOptions = { day: "numeric", month: "short", year: "numeric" }) {
  if (!iso) return "—";
  const d = parseLocal(iso);
  if (isNaN(+d)) return "—";
  return d.toLocaleDateString(locale(), opts);
}
export function fmtDay(iso?: string) { return fmtDate(iso, { day: "numeric", month: "short" }); }
export function fmtDateTime(iso?: string) {
  if (!iso) return "—";
  const d = new Date(iso);
  if (isNaN(+d)) return "—";
  const loc = locale();
  return d.toLocaleDateString(loc, { day: "numeric", month: "short", year: "numeric" }) + ", " +
    d.toLocaleTimeString(loc, { hour: "numeric", minute: "2-digit" });
}

export function relative(iso?: string) {
  if (!iso) return "";
  const days = Math.round((+parseLocal(iso) - Date.now()) / 86400000);
  const abs = Math.abs(days);
  const zh = getLang() === "zh";
  if (days === 0) return zh ? "今天" : "today";
  if (days === 1) return zh ? "明天" : "tomorrow";
  if (days === -1) return zh ? "昨天" : "yesterday";
  if (days < 0) return zh ? `${abs}天前` : `${abs}d ago`;
  if (days < 14) return zh ? `${days}天后` : `in ${days}d`;
  if (days < 60) return zh ? `${Math.round(days / 7)}周后` : `in ${Math.round(days / 7)}w`;
  return zh ? `${Math.round(days / 30)}个月后` : `in ${Math.round(days / 30)}mo`;
}
export function daysUntil(iso?: string) {
  if (!iso) return Infinity;
  return Math.round((+parseLocal(iso) - Date.now()) / 86400000);
}
export function initials(name: string) {
  return name.split(/\s+/).slice(0, 2).map((w) => w[0]?.toUpperCase() ?? "").join("");
}

export function money(n: number | undefined, currency = "USD") {
  if (n === undefined || isNaN(n)) return "—";
  return new Intl.NumberFormat("en-US", { style: "currency", currency, maximumFractionDigits: 0 }).format(n);
}

/* --- live clock ------------------------------------------------------ */
export function useNow(intervalMs = 1000) {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), intervalMs);
    return () => clearInterval(t);
  }, [intervalMs]);
  return now;
}

export interface TimeLeft { d: number; h: number; m: number; s: number; past: boolean; totalMs: number; }
export function timeLeft(iso: string, now = Date.now()): TimeLeft {
  const ms = +new Date(iso) - now;
  const past = ms <= 0;
  const t = Math.max(0, ms);
  return {
    d: Math.floor(t / 86400000),
    h: Math.floor(t / 3600000) % 24,
    m: Math.floor(t / 60000) % 60,
    s: Math.floor(t / 1000) % 60,
    past,
    totalMs: ms,
  };
}
